import React from "react";
import {
  Box,
  TableBody,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Tooltip,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import { Button, Typography } from "@platform/design-system";

import TableHeadersSort from "../tableHeadersSort";
import { PENDING_CARRIER } from "../../constants";
import ConnectedCarrierIcon from "../connectedCarrierIcon";
import {
  formatCurrencyWithRound,
  formatDate,
  mapLocToPolicyDetailsVariant,
} from "../../utils";

const headers = [
  {
    id: "name",
    label: "Coverage",
    isSortable: false,
  },
  {
    id: "carrier",
    label: "Carrier",
    isSortable: false,
  },
  {
    id: "strategic",
    label: "Carrier Tier",
    isSortable: false,
  },
  {
    id: "effectiveDate",
    label: "Effective Date",
    isSortable: false,
  },
  {
    id: "renewalDate",
    label: "Renewal Date",
    isSortable: false,
  },
  {
    id: "premium",
    label: "Premium",
    isSortable: false,
  },
];
const headersNonNA = [
  {
    id: "name",
    label: "Coverage",
    isSortable: false,
  },
  {
    id: "carrier",
    label: "Carrier",
    isSortable: false,
  },
  {
    id: "strategic",
    label: "Carrier Tier",
    isSortable: false,
  },

  {
    id: "renewalDate",
    label: "Renewal Date",
    isSortable: false,
  },
  {
    id: "premium",
    label: "Annualized Premium",
    isSortable: false,
  },
];

const AddCurrentCoverageTable = ({
  coverages,
  brokerHasMultipleStrategicTiers,
  tableHeight = "40vh",
  hideNonNACols = false,
  isConnectedCarriers = false,
  onSelectCoverage,
  selectedCoverage,
  isLoading = false,
}) => {
  const processedHeaders = [...(hideNonNACols ? headersNonNA : headers)];

  if (isConnectedCarriers) {
    processedHeaders.push({
      id: "viewDetails",
      label: "",
      isSortable: false,
    });
  }

  const checkIfFlagExist = () => {
    if (brokerHasMultipleStrategicTiers !== undefined) {
      return brokerHasMultipleStrategicTiers;
    }

    return coverages?.at(-1)?.brokerHasMultipleStrategicTiers ?? false;
  };

  const strategicText = (data) => {
    if (data.isStrategic) {
      return checkIfFlagExist() ? `Strategic - ${data.tier}` : "Strategic";
    }

    return "Non-Strategic";
  };

  return (
    <TableContainer sx={{ minHeight: tableHeight }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableHeadersSort headers={processedHeaders} data={coverages} />
          </TableRow>
        </TableHead>
        <TableBody>
          {coverages?.length > 0 ? (
            coverages.map((elem) => {
              const isEnabled =
                isConnectedCarriers &&
                elem.isCarrierEnabled &&
                mapLocToPolicyDetailsVariant(elem.lineOfCoverage);
              return (
                <TableRow
                  key={`${elem.coverage}-${elem.carrier}-${elem.groupNumber}`}
                >
                  <TableCell>
                    <Typography>
                      <strong>{elem.coverage || "N/A"}</strong>
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {elem.carrier?.toUpperCase() === PENDING_CARRIER ? (
                      <Box display="flex" alignItems="center">
                        <Typography>
                          <em>Pending</em>
                        </Typography>
                        <Tooltip
                          placement="top"
                          sx={{ fontSize: 19, marginLeft: 1 }}
                          title={
                            "The carrier you provided is not in our records. This field will be updated as soon as it is added."
                          }
                        >
                          <InfoIcon />
                        </Tooltip>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          columnGap: "0.25rem",
                        }}
                      >
                        {elem.carrier || "N/A"}
                        {isEnabled ? <ConnectedCarrierIcon /> : null}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>{strategicText(elem)}</TableCell>
                  {!hideNonNACols && (
                    <TableCell>{elem.effectiveDate || "N/A"}</TableCell>
                  )}
                  <TableCell>
                    {" "}
                    {elem.renewalDate
                      ? formatDate(elem.renewalDate, true)
                      : "N/A"}
                  </TableCell>
                  <TableCell>
                    {elem.premium
                      ? formatCurrencyWithRound(elem.premium)
                      : "N/A"}
                  </TableCell>
                  {isConnectedCarriers ? (
                    <TableCell align="right" sx={{ pr: 4 }}>
                      {isEnabled ? (
                        <Button
                          variant="tertiary"
                          size="sm"
                          loading={selectedCoverage === elem && isLoading}
                          disabled={!!selectedCoverage}
                          onClick={() => onSelectCoverage(elem)}
                          sx={{
                            minWidth: "max-content",
                            ml: 3,
                          }}
                        >
                          Policy Details
                        </Button>
                      ) : null}
                    </TableCell>
                  ) : null}
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell colSpan={7}>
                <Typography variant="body-medium">
                  <em>No coverages added for this Client</em>
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default AddCurrentCoverageTable;
